// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Opening view: the headline numbers, the 3G shutdown as a stacked area over
// eight years, and every automatically detected finding as a card that states
// the rule that produced it.

import type { TowerData, Generation } from '../types';
import { CARRIER_ALIAS, GEN_COLOR } from '../colors';
import { attr, esc, num, pct } from '../format';
import { gloss } from '../glossary';
import { statRow, viewHead, chartCard, legend, ticks } from './ui';

const GENS: Generation[] = ['3G', '4G', '5G'];

export function mount(el: HTMLElement, data: TowerData): void {
  const { meta, history } = data;
  const years = history.years;
  const yl = years.length - 1;

  // National per-generation totals across all three carriers, by year.
  const natGen: Record<string, number[]> = {};
  for (const g of GENS) {
    natGen[g] = years.map((_, i) =>
      meta.carriers.reduce((n, c) => n + history.carriers[c].gens[g][i], 0),
    );
  }
  const natSites = years.map((_, i) => meta.carriers.reduce((n, c) => n + history.carriers[c].sites[i], 0));
  const natLow = years.map((_, i) => meta.carriers.reduce((n, c) => n + history.carriers[c].modernLow[i], 0));

  const g5Share = natGen['5G'][yl] / natSites[yl];

  el.innerHTML = `
    ${viewHead({
      title: 'Every mobile tower in Australia',
      question: 'Who has built what, where, and who paid for it?',
      blurb: `Telstra, Optus and TPG must tell the ACCC where every mobile ${gloss('site', 'site')} is and which ${gloss('band', 'frequency bands')} it runs. This is all ${num(meta.totals.sites)} of them for ${meta.latestYear}, plus the eight years behind them — turned into the questions people actually ask.`,
    })}

    ${statRow([
      { label: 'Mobile sites', value: num(meta.totals.sites), sub: `${meta.latestYear} snapshot` },
      ...meta.carriers.map((c) => ({
        label: CARRIER_ALIAS[c],
        value: num(meta.totals.byCarrier[c]),
        sub: pct(meta.totals.byCarrier[c] / meta.totals.sites, 0) + ' of all sites',
        tip: `${CARRIER_ALIAS[c]} operates ${num(meta.totals.byCarrier[c])} of the ${num(meta.totals.sites)} registered mobile sites in ${meta.latestYear}.`,
      })),
      {
        label: 'Publicly co-funded',
        value: num(meta.totals.coFunded),
        sub: pct(meta.totals.coFunded / meta.totals.sites, 1) + ' of the network',
        tone: 'warn',
        tip: 'Sites where a government program contributed to the build cost.',
      },
      {
        label: 'Carrying 5G',
        value: num(natGen['5G'][yl]),
        sub: pct(g5Share, 0) + ' of sites',
        tone: 'good',
      },
    ])}

    ${chartCard(
      'The 3G shutdown, and what replaced it',
      'Sites carrying each generation, all three carriers combined. 3G does not taper — it falls off a cliff.',
      genChart(years, natGen, natLow),
    )}

    <section class="card">
      <div class="card-head">
        <h2>What the data says</h2>
        <p>Each finding below is produced by a rule run over the raw files, not written by hand. Hover a card to see the exact rule; click to open the view it came from.</p>
      </div>
      <div class="insight-grid">
        ${data.insights
          .map(
            (i) => `<article class="insight insight-${i.severity}" data-view="${attr(i.view)}" role="button" tabindex="0" data-tip="${attr(`Rule: ${i.rule}`)}">
              <h3>${esc(i.title)}</h3>
              <p>${esc(i.body)}</p>
              <span class="insight-go">Open ${esc(i.view.replace('-', ' '))} →</span>
            </article>`,
          )
          .join('')}
      </div>
    </section>
  `;

  const go = (e: Event) => {
    const card = (e.target as Element).closest('[data-view]') as HTMLElement | null;
    if (!card) return;
    location.hash = `view=${card.getAttribute('data-view')}`;
  };
  el.querySelectorAll('.insight').forEach((c) => {
    c.addEventListener('click', go);
    c.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        go(e);
      }
    });
  });
}

function genChart(years: number[], natGen: Record<string, number[]>, natLow: number[]): string {
  const W = 900;
  const H = 320;
  const P = { t: 16, r: 16, b: 34, l: 56 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const max = Math.max(...GENS.flatMap((g) => natGen[g]), ...natLow) * 1.06;
  const x = (i: number) => P.l + (i / (years.length - 1)) * iw;
  const y = (v: number) => P.t + ih - (v / max) * ih;

  const series = [
    ...GENS.map((g) => ({ key: g, color: GEN_COLOR[g], label: `Sites carrying ${g}`, vals: natGen[g], dash: '' })),
    { key: 'low', color: '#15803d', label: 'Sites with long-reach spectrum', vals: natLow, dash: '5 4' },
  ];

  const lines = series
    .map((s) => {
      const d = s.vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
      return `<path d="${d}" fill="none" stroke="${s.color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"${s.dash ? ` stroke-dasharray="${s.dash}"` : ''} />`;
    })
    .join('');

  const dots = series
    .flatMap((s) =>
      s.vals.map(
        (v, i) =>
          `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="4.5" fill="#fff" stroke="${s.color}" stroke-width="2"
             data-tip="${attr(`${years[i]}\n${s.label}\n${num(v)} sites`)}" />`,
      ),
    )
    .join('');

  const grid = ticks(max, 4)
    .map(
      (t) =>
        `<line x1="${P.l}" x2="${W - P.r}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" stroke="var(--border-subtle)" />
         <text x="${P.l - 8}" y="${(y(t) + 4).toFixed(1)}" class="ax" text-anchor="end">${num(t)}</text>`,
    )
    .join('');

  const xlab = years
    .map((yr, i) => `<text x="${x(i).toFixed(1)}" y="${H - 12}" class="ax" text-anchor="middle">${yr}</text>`)
    .join('');

  return `<div class="chart">
      <svg viewBox="0 0 ${W} ${H}" class="line-chart" role="img"
        aria-label="Sites carrying each mobile generation from ${years[0]} to ${years[years.length - 1]}">
        ${grid}${xlab}${lines}${dots}
      </svg>
    </div>
    ${legend(series.map((s) => ({ color: s.color, label: s.label })))}`;
}
